import React, { useState } from 'react';
import { X, Copy, Check, QrCode, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../utils/api';
import { QRCodeSVG } from 'qrcode.react';

interface RoomPaymentOrderData {
  orderId: number;
  amount: number;
  roomId: number;
  roomName: string;
  priceType: string;
  systemNote: string;
  paymentAddress: string;
  contractAddress: string;
  trc20Address?: string;
  erc20Address?: string;
  networkType: string;
}

interface RoomPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  roomName: string;
  roomDescription?: string;
  amount: number;
  priceType: string;
  roomId: number;
  trc20Address?: string;
  erc20Address?: string;
}

const RoomPaymentModal: React.FC<RoomPaymentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  roomName,
  roomDescription,
  amount,
  priceType,
  roomId,
  trc20Address,
  erc20Address
}) => {
  const [step, setStep] = useState<'input' | 'waiting' | 'success'>('input');
  const [orderData, setOrderData] = useState<RoomPaymentOrderData | null>(null);
  const [loading, setLoading] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState(false);
  const [copiedNote, setCopiedNote] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentCheckInterval, setPaymentCheckInterval] = useState<NodeJS.Timeout | null>(null);
  const [selectedNetwork, setSelectedNetwork] = useState<'trc20' | 'erc20'>('trc20');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const availableNetworks = [];
  if (trc20Address) availableNetworks.push('trc20');
  if (erc20Address) availableNetworks.push('erc20');

  const resetState = () => {
    setStep('input');
    setOrderData(null);
    setLoading(false);
    setCopiedAddress(false);
    setCopiedNote(false);
    setShowQr(false);
    if (paymentCheckInterval) {
      clearInterval(paymentCheckInterval);
      setPaymentCheckInterval(null);
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const createOrder = async () => {
    try {
      setLoading(true);
      const response = await api.post<any>('/payment/room-membership', {
        roomId,
        roomName,
        amount,
        priceType,
        networkType: selectedNetwork
      });

      if (response?.success && response?.data) {
        setOrderData({
          ...response.data,
          paymentAddress: response.data.selected_address || response.data.paymentAddress,
          networkType: response.data.network_type || selectedNetwork,
          trc20Address: response.data.trc20_address,
          erc20Address: response.data.erc20_address
        });
        setStep('waiting');
        startPaymentMonitoring();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || '创建订单失败');
    } finally {
      setLoading(false);
    }
  };

  const copyAddress = async () => {
    if (!orderData) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(orderData.paymentAddress);
        setCopiedAddress(true);
        toast.success('地址已复制');
        setTimeout(() => setCopiedAddress(false), 2000);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = orderData.paymentAddress;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (successful) {
          setCopiedAddress(true);
          toast.success('地址已复制');
          setTimeout(() => setCopiedAddress(false), 2000);
        } else {
          toast.error('复制失败，请手动复制');
        }
      }
    } catch (error) {
      toast.error('复制失败，请手动复制');
    }
  };

  const copyNote = async () => {
    if (!orderData) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(orderData.systemNote);
        setCopiedNote(true);
        toast.success('备注已复制');
        setTimeout(() => setCopiedNote(false), 2000);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = orderData.systemNote;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (successful) {
          setCopiedNote(true);
          toast.success('备注已复制');
          setTimeout(() => setCopiedNote(false), 2000);
        } else {
          toast.error('复制失败，请手动复制');
        }
      }
    } catch (error) {
      toast.error('复制失败，请手动复制');
    }
  };

  const checkUsdtTransaction = async (): Promise<{ found: boolean; txid?: string; fromAddress?: string }> => {
    if (!orderData) return { found: false };

    try {
      const address = orderData.paymentAddress.toLowerCase();
      const expectedAmount = orderData.amount.toString();
      const expectedMemo = orderData.systemNote;
      
      const minConfirmations = 2;

      if (orderData.networkType === 'trc20') {
        const url = `https://api.trongrid.io/v1/accounts/${orderData.paymentAddress}/transactions/trc20?contract_address=TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t&limit=20`;

        const response = await fetch(url);
        if (!response.ok) return { found: false };

        const data = await response.json();

        if (data.data && data.data.length > 0) {
          const currentTime = Date.now();
          const matchingTx = data.data.find((tx: any) => {
            if (tx.type !== 'in') return false;
            
            const txAddress = (tx.to || '').toLowerCase();
            if (txAddress !== address) return false;

            const txAmount = (parseInt(tx.value) / 1e6);
            const expected = parseFloat(expectedAmount);
            if (Math.abs(txAmount - expected) >= 0.000001) return false;

            const txMemo = tx.memo || '';
            if (txMemo !== expectedMemo) {
              console.log('Memo mismatch:', txMemo, 'vs', expectedMemo);
              return false;
            }

            const blockTime = tx.block_timestamp;
            if (!blockTime) return false;
            const confirmTime = minConfirmations * 3000;
            if (currentTime - blockTime < confirmTime) {
              console.log('Insufficient confirmations:', currentTime - blockTime, '<', confirmTime);
              return false;
            }

            return true;
          });

          if (matchingTx) {
            console.log('Payment verified:', matchingTx.transaction_id);
            return {
              found: true,
              txid: matchingTx.transaction_id,
              fromAddress: matchingTx.from
            };
          }
        }
      } else if (orderData.networkType === 'erc20') {
        const apiKey = process.env.REACT_APP_ETHERSCAN_API_KEY || 'YourApiKeyToken';
        const url = `https://api.etherscan.io/api?module=account&action=tokentx&address=${orderData.paymentAddress}&contractaddress=0xdAC17F958D2ee523a2206206994597C13D831ec7&page=1&offset=20&sort=desc&apikey=${apiKey}`;

        const response = await fetch(url);
        if (!response.ok) return { found: false };

        const data = await response.json();

        if (data.status === '1' && data.result && data.result.length > 0) {
          const latestBlockUrl = 'https://api.etherscan.io/api?module=proxy&action=eth_blockNumber&apikey=' + apiKey;
          const latestBlockResponse = await fetch(latestBlockUrl);
          const latestBlockData = await latestBlockResponse.json();
          const latestBlock = parseInt(latestBlockData.result, 16);

          const matchingTx = data.result.find((tx: any) => {
            const txAddress = (tx.to || '').toLowerCase();
            if (txAddress !== address) return false;

            const txAmount = (parseInt(tx.value) / 1e6);
            const expected = parseFloat(expectedAmount);
            if (Math.abs(txAmount - expected) >= 0.000001) return false;

            const blockNum = parseInt(tx.blockNumber);
            
            if (latestBlock - blockNum < minConfirmations) {
              console.log('Insufficient confirmations for ERC20');
              return false;
            }

            return true;
          });

          if (matchingTx) {
            console.log('Payment verified (ERC20):', matchingTx.hash);
            return {
              found: true,
              txid: matchingTx.hash,
              fromAddress: matchingTx.from
            };
          }
        }
      }

      return { found: false };
    } catch (error) {
      console.error('查询USDT交易失败:', error);
      return { found: false };
    }
  };

  const confirmOrderToBackend = async (txid: string, fromAddress: string): Promise<boolean> => {
    try {
      const response = await api.post<any>('/payment/room-membership/confirm', {
        orderId: orderData!.orderId,
        txid,
        fromAddress
      });
      return response?.success === true;
    } catch (error) {
      console.error('确认订单失败:', error);
      return false;
    }
  };

  const finalizeOrderOnBackend = async (): Promise<boolean> => {
    try {
      const response = await api.post<any>('/payment/room-membership/complete', {
        orderId: orderData!.orderId
      });
      return response?.success === true;
    } catch (error) {
      console.error('完成订单失败:', error);
      return false;
    }
  };

  const startPaymentMonitoring = () => {
    if (paymentCheckInterval) return;

    toast.success(`开始监控${selectedNetwork === 'trc20' ? 'TRC20' : 'ERC20'}支付，请在30分钟内完成支付`);

    const interval = setInterval(async () => {
      const result = await checkUsdtTransaction();
      if (result.found && result.txid) {
        clearInterval(interval);
        setPaymentCheckInterval(null);
        toast.success('检测到支付，准备确认订单...');

        const confirmed = await confirmOrderToBackend(result.txid, result.fromAddress || '');
        if (confirmed) {
          toast.success('链上确认中，等待2个区块确认...');
          setTimeout(async () => {
            const finalized = await finalizeOrderOnBackend();
            if (finalized) {
              toast.success('购买成功！');
              setStep('success');
              setTimeout(() => {
                if (onSuccess) onSuccess();
                handleClose();
              }, 2000);
            } else {
              toast.error('订单已完成，但处理失败，请联系客服');
            }
          }, orderData?.networkType === 'trc20' ? 15000 : 30000);
        } else {
          toast.error('确认订单失败，请重试');
        }
      }
    }, 10000);

    setPaymentCheckInterval(interval);

    setTimeout(() => {
      if (interval) {
        clearInterval(interval);
        setPaymentCheckInterval(null);
        if (orderData) {
          toast.error('支付超时，请重新下单');
          handleClose();
        }
      }
    }, 30 * 60 * 1000);
  };

  const handleManualCheck = async () => {
    setCheckingPayment(true);
    try {
      const result = await checkUsdtTransaction();
      if (result.found && result.txid) {
        toast.success('检测到支付，准备确认订单...');

        const confirmed = await confirmOrderToBackend(result.txid, result.fromAddress || '');
        if (confirmed) {
          toast.success('链上确认中，等待2个区块确认...');
          setTimeout(async () => {
            const finalized = await finalizeOrderOnBackend();
            if (finalized) {
              toast.success('购买成功！');
              setStep('success');
              setTimeout(() => {
                if (onSuccess) onSuccess();
                handleClose();
              }, 2000);
            } else {
              toast.error('订单已完成，但处理失败，请联系客服');
            }
          }, orderData?.networkType === 'trc20' ? 15000 : 30000);
        } else {
          toast.error('确认订单失败，请重试');
        }
      } else {
        toast.error('未检测到支付，请确认已转账');
      }
    } finally {
      setCheckingPayment(false);
    }
  };

  if (!isOpen) return null;

  const getPriceTypeLabel = (type: string) => {
    switch (type) {
      case 'monthly':
        return '月付';
      case 'yearly':
        return '年付';
      case 'one_time':
        return '一次性';
      default:
        return type;
    }
  };

  const getNetworkLabel = (network: string) => {
    switch (network) {
      case 'trc20':
        return 'TRC20 (TRON)';
      case 'erc20':
        return 'ERC20 (Ethereum)';
      default:
        return network;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800">💎 购买会员</h2>
          <button
            onClick={handleClose}
            className="p-1 text-gray-500 hover:text-gray-800 rounded hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          {step === 'input' && (
            <div>
              <div className="text-sm text-gray-500 mb-4">
                确认购买 <strong>{roomName}</strong> 房间会员
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">房间名称</span>
                  <span className="font-medium">{roomName}</span>
                </div>
                {roomDescription && (
                  <div className="py-2 border-b border-gray-100">
                    <span className="text-gray-600">房间描述</span>
                    <p className="text-gray-800 mt-1 text-sm">{roomDescription}</p>
                  </div>
                )}
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">收费模式</span>
                  <span className="font-medium">{getPriceTypeLabel(priceType)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">应付金额</span>
                  <span className="text-xl font-bold text-primary-600">{amount.toFixed(2)} USDT</span>
                </div>
                <div className="py-2 border-b border-gray-100">
                  <span className="text-gray-600 block mb-2">选择支付网络</span>
                  <div className="flex gap-2">
                    {availableNetworks.map((network) => (
                      <button
                        key={network}
                        onClick={() => setSelectedNetwork(network as 'trc20' | 'erc20')}
                        className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                          selectedNetwork === network
                            ? network === 'trc20'
                              ? 'bg-blue-500 text-white'
                              : 'bg-purple-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {getNetworkLabel(network)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-2 mb-4">
                <input
                  type="checkbox"
                  id="agree-terms"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 text-pink-500 border-gray-300 rounded focus:ring-pink-500"
                />
                <label htmlFor="agree-terms" className="text-sm text-gray-600">
                  我已阅读并同意{' '}
                  <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-pink-500 hover:underline">
                    《服务协议》
                  </a>{' '}
                  和{' '}
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-pink-500 hover:underline">
                    《隐私政策》
                  </a>
                </label>
              </div>

              <button
                onClick={createOrder}
                disabled={loading || availableNetworks.length === 0 || !agreedToTerms}
                className="w-full py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg font-medium hover:from-pink-600 hover:to-purple-600 transition-colors disabled:opacity-50"
              >
                {loading ? '创建订单中...' : availableNetworks.length === 0 ? '房间未配置收款地址' : !agreedToTerms ? '请先同意协议' : '生成支付订单'}
              </button>
            </div>
          )}

          {step === 'waiting' && orderData && (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <div className="text-gray-600 mb-2">请完成以下转账</div>
                <div className="text-3xl font-bold text-pink-600">
                  {orderData.amount.toFixed(6)} USDT
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  房间：{orderData.roomName} | 网络：{getNetworkLabel(orderData.networkType)}
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-3">
                <div className="text-sm text-gray-600 mb-2">
                  收款地址 ({getNetworkLabel(orderData.networkType)})
                </div>
                <div className="bg-gray-100 rounded p-2 text-sm break-all mb-2">
                  {orderData.paymentAddress}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={copyAddress}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-pink-500 text-white rounded hover:bg-pink-600 transition-colors text-sm"
                  >
                    {copiedAddress ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copiedAddress ? '已复制' : '复制地址'}
                  </button>
                  <button
                    onClick={() => setShowQr(!showQr)}
                    className="flex items-center justify-center gap-1 px-3 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors text-sm"
                  >
                    <QrCode className="w-4 h-4" />
                    {showQr ? '隐藏' : '二维码'}
                  </button>
                </div>
              </div>

              {showQr && (
                <div className="flex justify-center">
                  <div className="bg-white p-4 border border-pink-200 rounded-lg">
                    <QRCodeSVG value={orderData.paymentAddress} size={192} level="H" />
                  </div>
                </div>
              )}

              <div className="border border-gray-200 rounded-lg p-3">
                <div className="text-sm text-gray-600 mb-2">系统备注（必填）</div>
                <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-sm break-all mb-2">
                  {orderData.systemNote}
                </div>
                <button
                  onClick={copyNote}
                  className="flex items-center justify-center gap-1 px-3 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-colors text-sm"
                >
                  {copiedNote ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copiedNote ? '已复制' : '复制备注'}
                </button>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="text-sm font-medium text-yellow-800 mb-2">
                  ⚠️ 重要提示
                </div>
                <ul className="text-xs text-yellow-800 space-y-1">
                  <li>• 请使用 {getNetworkLabel(orderData.networkType)} 网络，选错网络资产无法找回</li>
                  <li>• 转账金额必须与显示完全一致</li>
                  <li>• 必须复制系统生成备注并填写在备注中</li>
                  <li>• 链上确认后会自动同步订单状态</li>
                </ul>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleManualCheck}
                  disabled={checkingPayment}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${checkingPayment ? 'animate-spin' : ''}`} />
                  {checkingPayment ? '检查中...' : '手动检查'}
                </button>
                <button
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  关闭
                </button>
              </div>

              <div className="text-center text-sm text-gray-500">
                <div className="flex items-center justify-center gap-2">
                  <div className="w-2 h-2 bg-pink-500 rounded-full animate-pulse"></div>
                  自动监控中，每10秒检查一次
                </div>
              </div>
            </div>
          )}

          {step === 'success' && (
            <div className="text-center py-8">
              <div className="text-6xl mb-4">🎉</div>
              <div className="text-2xl font-bold text-green-600 mb-2">购买成功！</div>
              <div className="text-gray-600">您已成功加入 {roomName}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoomPaymentModal;